/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  Icon,
  Spacer,
  TagLabel,
  Typography,
  Colors,
} from '@zapier/design-system';
import { Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import MoreButton from './MoreButton';
import { isLinkSelected } from './utils';

import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { SidebarV3 } from './types';

const MENU_WIDTH = '280px';
const PopoverStyles = css({
  borderRadius: '4px',
  background: 'var(--zds-background-weaker, #FFFDF9)',
  boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.20)',
});
const MenuStyles = css({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'inherit',
  boxSizing: 'border-box',
  overflow: 'auto',
  padding: 'var(--zds-space-8, 8px)',
  width: MENU_WIDTH,
  outline: 'none',
});

const MenuItemStyles = css({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'var(--zds-space-12, 12px)',
  padding: 'var(--zds-space-8, 8px)',
  borderRadius: '4px',
  cursor: 'pointer',
  position: 'relative',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    pointerEvents: 'none',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    border: '2px solid transparent',
    borderRadius: 4,
    zIndex: 2,
  },
  '&[data-active="true"]': {
    backgroundColor: 'var(--zds-ui-brand-weakest, #FDF5F2)',
  },
  '&[data-focus-visible]:not([data-active="true"]), &[data-hovered]:not([data-active="true"])':
    {
      backgroundColor: 'var(--zds-background-strong, #F5F3EB)',
    },
  '&[data-focus-visible]': {
    '&::before': {
      border: 'var(--zds-border-focus, 2px solid #695BE8)',
      top: '-2px',
      right: '-2px',
      bottom: '-2px',
      left: '-2px',
    },
  },
});

const IconWrapperStyles = (isSelected: boolean) =>
  css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--zds-space-8, 8px)',
    borderRadius: '4px',
    backgroundColor: isSelected
      ? 'var(--zds-ui-brand-weakest, #FDF5F2)'
      : 'var(--zds-background-strong, #F5F3EB)',
    '& svg': {
      fill: isSelected ? Colors.zapier : 'var(--zds-text-default, #413735)',
    },
  });

const RightColumnStyles = (isSelected: boolean) =>
  css({
    display: 'flex',
    flexDirection: 'column',
    font: isSelected
      ? `var(--zds-body-3, 600 14px/24px ${Typography.base.fontFamily})`
      : `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
    color: 'var(--zds-text-default, #413735)',
  });

const TitleRowStyles = css({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
});

const AfterIconPositionStyles = css({
  position: 'absolute',
  right: 0,
  '& svg': {
    fill: 'var(--zds-text-weakest, #6f6765)',
  },
});

const DescriptionWrapperStyles = css({
  font: `var(--zds-minimal-print-1, 400 12px/20px ${Typography.base.fontFamily})`,
  color: 'var(--zds-text-weaker, #574E4C)',
});

type MoreItemProps = SidebarV3.MoreOption & {
  isSelected: boolean;
};

const MoreItem = ({
  description,
  href,
  icon,
  iconFilled,
  isSelected = false,
  tag,
  target,
  text,
}: MoreItemProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  return (
    <MenuItem
      css={MenuItemStyles}
      data-active={isSelected}
      href={getFullyQualifiedURL(href)}
      key={text}
      id={text}
      onAction={() => {
        emitUserInteractionEvent({
          event_action: 'click',
          option_selected: `UniversalSidebar MoreOption: ${text}`,
        });
      }}
      target={target}
      data-zds
    >
      <div data-zds css={IconWrapperStyles(isSelected)}>
        <Icon
          name={isSelected ? iconFilled : icon}
          size={20}
          shouldAnimateFill={false}
        />
      </div>
      <div css={RightColumnStyles(isSelected)} data-zds>
        <div css={TitleRowStyles} data-zds>
          <span data-zds>{text}</span>
          {tag && (
            <span style={{ display: 'flex' }}>
              <Spacer width={8} />
              <TagLabel size="small" color="simple">
                {tag}
              </TagLabel>
            </span>
          )}
          {target === '_blank' && (
            <span data-zds css={AfterIconPositionStyles}>
              <Icon name="arrowOffsite" size={16} />
            </span>
          )}
        </div>
        <span data-zds css={DescriptionWrapperStyles}>
          {description}
        </span>
      </div>
    </MenuItem>
  );
};

type MoreLinksMenuProps = {
  isCollapsed: boolean;
  items: SidebarV3.MoreLinks;
  selectedHref: string;
};

export const MoreLinksMenu = ({
  isCollapsed,
  items,
  selectedHref,
}: MoreLinksMenuProps) => {
  const { isMoreLinksMenuOpen, setIsMoreLinksMenuOpen } =
    useUniversalNavContext();
  return (
    <MenuTrigger
      isOpen={isMoreLinksMenuOpen}
      onOpenChange={setIsMoreLinksMenuOpen}
    >
      <MoreButton
        icon={items.icon}
        isCollapsed={isCollapsed}
        onClick={() => {
          emitUserInteractionEvent({
            event_action: 'click',
            option_selected: 'UniversalSidebar MoreLinksMenu click',
          });
        }}
        text={items.text}
      />
      <Popover css={PopoverStyles}>
        <Menu css={MenuStyles}>
          {items.moreLinksItems.map((item) => (
            <MoreItem
              {...item}
              key={item.text}
              isSelected={isLinkSelected(item.href, selectedHref)}
            />
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};
